// frontend/src/pages/staff/StaffProfileEdit.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import StaffSidebarLayout from './StaffSidebarLayout';
import axios from 'axios';
import { prepareImageForUpload } from '../../utils/imageUploadPolicy';
import ImageUploadField from '../../components/ImageUploadField';
import './staffStyles.css';

function StaffProfileEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '', // Read-only
    timezone: ''
  });

  // All professionals use unified professional role type
  const getRoleType = useCallback(() => {
    return 'professional';
  }, []);

  const getBasePath = useCallback(() => {
    const roleType = getRoleType();
    return `/dashboard/${roleType}`;
  }, [getRoleType]);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg('Configuration error. Please contact support.');
      return null;
    }
    return baseURL;
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/me`, { headers });
      
      // Populate form with existing data
      setFormData({
        firstName: res.data.user.firstName || '',
        lastName: res.data.user.lastName || '',
        phone: res.data.user.phone || '',
        email: res.data.user.email || '',
        timezone: res.data.staff?.timezone || '',
      });
      
      // Set existing profile picture if available
      if (res.data.user.profilePicture) {
        setProfilePicturePreview(res.data.user.profilePicture);
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem('ogestyle_token');
        navigate('/login');
      } else {
        setErrorMsg('Unable to load profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear messages on input change
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrorMsg('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMsg('Image size must be less than 5MB');
        return;
      }
      
      setProfilePicture(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setErrorMsg('');
    }
  };

  const uploadProfilePicture = async () => {
    if (!profilePicture) return null;
    
    try {
      setUploadingImage(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return null;
      
      const formData = new FormData();
      formData.append('profilePicture', await prepareImageForUpload(profilePicture));
      
      const res = await axios.post(
        `${baseURL}/api/staff/upload-profile-picture`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      return res.data.profilePictureUrl;
    } catch (err) {
      throw new Error('Failed to upload profile picture');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Never submit without an actively chosen timezone (browser guard, not backend).
    if (!formData.timezone || !formData.timezone.trim()) {
      setErrorMsg('Please choose your timezone.');
      return;
    }

    setErrorMsg('');
    setSuccessMsg('');
    setSaving(true);

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) {
        setSaving(false);
        return;
      }

      // Validate inputs
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setErrorMsg('First name and last name are required');
        setSaving(false);
        return;
      }

      // Upload profile picture if changed
      let profilePictureUrl = profilePicturePreview;
      if (profilePicture) {
        profilePictureUrl = await uploadProfilePicture();
      }

      // Send update request
      await axios.patch(
        `${baseURL}/api/staff/profile`,
        {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: formData.phone.trim(),
          timezone: formData.timezone,
          profilePicture: profilePictureUrl
        },
        { headers }
      );

      setSuccessMsg('Profile updated successfully!');
      
      // Redirect back to profile view after 1.5 seconds
      setTimeout(() => {
        const basePath = getBasePath();
        navigate(`${basePath}/profile`);
      }, 1500);
      
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem('ogestyle_token');
        navigate('/login');
      } else {
        const errorMessage = err?.response?.data?.message || err.message || 'Unable to update profile. Please try again.';
        setErrorMsg(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const basePath = getBasePath();
    navigate(`${basePath}/profile`);
  };

  const roleType = getRoleType();

  if (loading) {
    return (
      <StaffSidebarLayout roleType={roleType}>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>Loading profile...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout roleType={roleType}>
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
        {/* Back to Profile Button */}
        <button
          onClick={() => navigate(`${getBasePath()}/profile`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            marginBottom: '20px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            background: '#fff',
            color: '#333',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#f5f5f5';
            e.target.style.borderColor = '#001d4a';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#fff';
            e.target.style.borderColor = '#ddd';
          }}
        >
          <span>←</span> Back to Profile
        </button>

        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: '0 0 8px 0' }}>Edit Profile</h2>
          <p style={{ margin: 0, color: '#666' }}>Update your personal information</p>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div style={{ 
            background: '#fff3f3', 
            border: '1px solid #c00', 
            borderRadius: '8px', 
            padding: '15px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, color: '#c00' }}>{errorMsg}</p>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div style={{ 
            background: '#e6ffed', 
            border: '1px solid #03543f', 
            borderRadius: '8px', 
            padding: '15px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, color: '#03543f' }}>{successMsg}</p>
          </div>
        )}

        {/* Form */}
        <div className="dashboard-card" style={{ padding: '30px' }}>
          <form onSubmit={handleSubmit}>
            {/* Profile Picture Upload */}
            {/* One shared field: input + validation + HEIC + preview + remove + guidance */}
            <ImageUploadField
              label="Profile Picture"
              variant="avatar"
              uploadOnSelect={false}
              localFiles={profilePicture ? [profilePicture] : []}
              onSelectFiles={(file) => setProfilePicture(file || null)}
              onError={(message) => setErrorMsg(message)}
            />

            {/* First Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px',
                color: '#001d4a'
              }}>
                First Name <span style={{ color: '#c00' }}>*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter your first name"
              />
            </div>

            {/* Last Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px',
                color: '#001d4a'
              }}>
                Last Name <span style={{ color: '#c00' }}>*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter your last name"
              />
            </div>

            {/* Phone */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px',
                color: '#001d4a'
              }}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Timezone */}
            <div style={{ marginBottom: '30px' }}>              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px',
                color: '#001d4a'
              }}>
                Timezone
              </label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={(e) => handleInputChange(e)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">Select your timezone...</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Phoenix">Arizona Time (MST)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
              </select>
              <p style={{ 
                margin: '5px 0 0 0', 
                fontSize: '12px', 
                color: '#666' 
              }}>
                Your schedule and bookings will use this timezone.
              </p>
            </div>

            {/* Email (Read-only) */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px',
                color: '#001d4a'
              }}>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  background: '#f5f5f5',
                  color: '#666',
                  cursor: 'not-allowed',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ 
                margin: '5px 0 0 0', 
                fontSize: '12px', 
                color: '#666' 
              }}>
                Email cannot be changed. Contact support if needed.
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: '12px 30px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#333',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary"
                style={{
                  padding: '12px 30px',
                  minWidth: '120px'
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </StaffSidebarLayout>
  );
}

export default StaffProfileEdit;
